// components/ContactUs.jsx
import React, { useState } from 'react';
import { postGeneralInquiry } from '../apiHelper';
import { Helmet } from 'react-helmet-async';

export default function ContactUs() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Simulate form submission (replace with your actual backend/API call)
        try {
            // Example: await fetch('/api/contact', { method: 'POST', body: JSON.stringify(formData) });
            // await new Promise(resolve => setTimeout(resolve, 1200)); // fake delay
            // console.log(formData)
            await postGeneralInquiry(formData)

            setSubmitted(true);
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (err) {
            setError('Something went wrong. Please try again or email us directly.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="py-20 md:py-28 bg-gray-50 min-h-screen">
            <Helmet>
                <title>Contact Us | Arxis Technologies Limited</title>
                <meta name="description" content="Have questions about our plans, need help getting started, or want to discuss custom needs? We're here to help — drop us a message." />
            </Helmet>
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        Get in Touch with Arxis
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Have questions about our plans, need help getting started, or want to discuss custom needs?
                        We're here to help — drop us a message and we'll get back to you within 24 hours.
                    </p>
                </div>

                <div className="grid md:grid-cols-5 gap-12">
                    {/* Contact Info / Left Column */}
                    <div className="md:col-span-2 space-y-10">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Reach Out</h2>
                            <p className="text-gray-600 mb-6">
                                Our team is based in Lagos and ready to assist Nigerian businesses like yours.
                            </p>

                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                                        <span className="text-2xl">📧</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Email</h3>
                                        {/* <p className="text-gray-600">support@arxisaccounting.com</p> */}
                                        <p className="text-gray-600">arxistechnologies@gmail.com</p>
                                        {/* <p className="text-gray-600">sales@arxisaccounting.com (for enterprise inquiries)</p> */}
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                                        <span className="text-2xl">📞</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Phone</h3>
                                        <p className="text-gray-600">+234 916 449 5789 (Mon–Fri, 9 AM – 5 PM WAT)</p>
                                    </div>
                                </div>

                                {/* <div className="flex items-start">
                                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                                        <span className="text-2xl">🏢</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">Office</h3>
                                        <p className="text-gray-600">Lagos, Nigeria</p>
                                        <p className="text-gray-600 text-sm mt-1">(Virtual-first team – virtual meetings preferred)</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>

                        {/* Alternative contact note */}
                        <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                            <p className="text-gray-700">
                                <strong>Need urgent help?</strong> Log in to your Arxis dashboard and use the live chat support — available during business hours.
                            </p>
                        </div>
                    </div>

                    {/* Form – Right Column */}
                    <div className="md:col-span-3">
                        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100">
                            {submitted ? (
                                <div className="text-center py-12">
                                    <div className="text-6xl mb-6">🎉</div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                        Thank You!
                                    </h2>
                                    <p className="text-gray-600 text-lg">
                                        Your message has been sent successfully. We'll get back to you very soon.
                                    </p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className="mt-8 px-8 py-3 bg-[#4285F4] text-white font-medium rounded-lg hover:bg-[#336bd1] transition"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4285F4]/30 focus:border-[#4285F4] outline-none transition"
                                            placeholder="Your name"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4285F4]/30 focus:border-[#4285F4] outline-none transition"
                                            placeholder="you@business.com"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                                            Subject *
                                        </label>
                                        <input
                                            type="text"
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4285F4]/30 focus:border-[#4285F4] outline-none transition"
                                            placeholder="e.g. Question about Enterprise Plan"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                            Your Message *
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows={6}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4285F4]/30 focus:border-[#4285F4] outline-none transition resize-none"
                                            placeholder="Tell us how we can help you..."
                                        />
                                    </div>

                                    {error && (
                                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`w-full py-4 px-6 text-white font-semibold rounded-lg transition shadow-md ${loading
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-[#4285F4] hover:bg-[#336bd1]'
                                            }`}
                                    >
                                        {loading ? 'Sending...' : 'Send Message'}
                                    </button>

                                    <p className="text-center text-sm text-gray-500 mt-4">
                                        We respect your privacy — your information will only be used to respond to this inquiry.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}